 
 /* eslint-disable */
 
 import React, { useState, useEffect, useRef } from "react";
 import { db, storage } from "../../firebase-config";
 import { ref, onValue, off, push, set, update, serverTimestamp, onDisconnect } from "firebase/database";
 import { ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
 import moment from "moment";
 import "./ChatNot.css";
 import { v4 as uuidv4 } from 'uuid'; // Import uuid for unique message ID
 
 const ChatNot = () => {
   // State variables to manage doctors, technicians, admins, selected user, messages, input, chat UI state, etc.
   const [doctorNames, setDoctorNames] = useState([]);
   const [technicianNames, setTechnicianNames] = useState([]);
   const [adminNames, setAdminNames] = useState([]);
   const [selectedUser, setSelectedUser] = useState(null);
   const [selectedUserName, setSelectedUserName] = useState("");
   const [selectedUserEmail, setSelectedUserEmail] = useState("");
   const [messages, setMessages] = useState([]);
   const [inputMessage, setInputMessage] = useState("");
   const [isMinimized, setIsMinimized] = useState(true);
   const [isChatOpen, setIsChatOpen] = useState(false);
   const [showUploadOptions, setShowUploadOptions] = useState(false);
   const [searchQuery, setSearchQuery] = useState("");
   const [activeList, setActiveList] = useState('doctors'); // Set default to 'doctors'
   const [allMessages, setAllMessages] = useState([]);
   const [newMessagesCount, setNewMessagesCount] = useState(0);
   const [usersWithUnreadMessages, setUsersWithUnreadMessages] = useState({});
   const [userPresence, setUserPresence] = useState({});
 
   const adminName = localStorage.getItem("adminName");
   const userType = localStorage.getItem("role");
   const userEmail = localStorage.getItem("senderEmail");
   const userName = localStorage.getItem("userName") || userEmail;
 
   const messageContainerRef = useRef(null);
 
   // Function to sanitize email by replacing '.' with '_'
   const sanitizeEmail = (email) => email ? email.replace(/\./g, '_') : '';
 
   // useEffect to set the active list based on user type
   useEffect(() => {
     if (userType === 'doctor') {
       setActiveList('admins');
     } else if (userType === 'technician') {
       setActiveList('doctors');
     } else {
       setActiveList('doctors');
     }
   }, [userType]);
 
   // Fetch doctor names from Firebase
   useEffect(() => {
     const fetchDoctorNames = () => {
       const doctorRef = ref(db, `superadmin/admins/${adminName}/doctors`);
       onValue(doctorRef, (snapshot) => {
         if (snapshot.exists()) {
           const data = snapshot.val();
           const names = Object.keys(data).filter(name => data[name].Email !== userEmail);
           setDoctorNames(names.map(name => ({ name, email: data[name].Email })));
         }
       });
     };
     fetchDoctorNames();
   }, [adminName, userEmail]);
 
   // Fetch technician names from Firebase
   useEffect(() => {
     const fetchTechnicianNames = () => {
       const technicianRef = ref(db, `superadmin/admins/${adminName}/technicians`);
       onValue(technicianRef, (snapshot) => {
         if (snapshot.exists()) {
           const data = snapshot.val();
           const names = Object.keys(data).filter(name => data[name].Email !== userEmail);
           setTechnicianNames(names.map(name => ({ name, email: data[name].Email })));
         }
       });
     };
     fetchTechnicianNames();
   }, [adminName, userEmail]);
 
   // Fetch admin names from Firebase
   useEffect(() => {
     if (adminName) {
       const adminRef = ref(db, `superadmin/admins/${adminName}`);
       onValue(adminRef, (snapshot) => {
         if (snapshot.exists()) {
           const adminData = snapshot.val();
           const adminEmail = adminData.Email;
           setAdminNames([{ name: adminName, email: adminEmail }].filter(name => name.name !== userName));
         } else {
           console.log("Admin data not found");
         }
       }, {
         onlyOnce: true
       });
     }
   }, [adminName, userName]);
 
   // Fetch all messages and set up a listener for real-time updates
   useEffect(() => {
     const messagesRef = ref(db, `superadmin/admins/${adminName}/chats`);
    //  console.log("Setting up listener for all messages...");
     onValue(messagesRef, (snapshot) => {
       if (snapshot.exists()) {
         const data = snapshot.val();
         const allMsgs = [];
         let newMsgCount = 0;
         const unreadUsers = {};
         Object.keys(data).forEach(chatId => {
           Object.entries(data[chatId]).forEach(([messageId, msg]) => {
             allMsgs.push({ ...msg, chatId, messageId });
             if (msg.receiver === userEmail && !msg.read) {
               newMsgCount++;
               if (!unreadUsers[msg.sender]) {
                 unreadUsers[msg.sender] = 1;
               } else {
                 unreadUsers[msg.sender]++;
               }
             }
           });
         });
        //  console.log("Fetched all messages:", allMsgs);
         setAllMessages(allMsgs);
         setNewMessagesCount(newMsgCount);
         setUsersWithUnreadMessages(unreadUsers);
       } else {
         console.log("No messages found.");
         setAllMessages([]);
         setNewMessagesCount(0);
         setUsersWithUnreadMessages({});
       }
     });
     return () => {
      //  console.log("Removing listener for all messages...");
       off(messagesRef);
     };
   }, [adminName, userEmail]);
 
   // Fetch user presence data
   useEffect(() => {
     const fetchUserPresence = () => {
       const userPresenceRef = ref(db, `superadmin/admins/${adminName}/chats`);
       onValue(userPresenceRef, (snapshot) => {
         if (snapshot.exists()) {
           const presenceData = snapshot.val();
           setUserPresence(presenceData);
         }
       });
     };
 
     fetchUserPresence();
   }, [adminName]);
 
   // Function to select a user and fetch messages between the logged-in user and selected user
   const selectUser = (user) => {
     console.log("Selected user:", user);
     setSelectedUser(user.name);
     setSelectedUserName(user.name);
     setSelectedUserEmail(user.email);
 
     const userPresenceRef = ref(db, `superadmin/admins/${adminName}/chats/${user.name}/presence/${sanitizeEmail(userEmail)}`);
     set(userPresenceRef, { state: "online", last_changed: serverTimestamp() });
     onDisconnect(userPresenceRef).set({ state: "offline", last_changed: serverTimestamp() });
 
     const fetchMessagesForLoggedInUserAndSelectedUser = () => {
       const userMessages = allMessages.filter(msg =>
         (msg.sender === userEmail && msg.receiver === user.email) ||
         (msg.sender === user.email && msg.receiver === userEmail)
       );
 
       const sortedMessages = userMessages.sort((a, b) => moment(a.timestamp, 'YYYY-MM-DD HH:mm:ss.sss').valueOf() - moment(b.timestamp, 'YYYY-MM-DD HH:mm:ss.sss').valueOf());
 
       console.log("Messages between logged-in user and selected user:", sortedMessages);
       setMessages(sortedMessages);
 
       const unreadMessages = userMessages.filter(msg => msg.receiver === userEmail && !msg.read);
       unreadMessages.forEach(msg => {
         const msgRef = ref(db, `superadmin/admins/${adminName}/chats/${msg.chatId}/${msg.messageId}`);
         update(msgRef, { read: true });
       });
 
       setNewMessagesCount(prevCount => prevCount - unreadMessages.length);
       setUsersWithUnreadMessages(prevUnreadUsers => {
         const updatedUnreadUsers = { ...prevUnreadUsers };
         delete updatedUnreadUsers[user.email];
         return updatedUnreadUsers;
       });
     };
 
     fetchMessagesForLoggedInUserAndSelectedUser();
   };
 
   // Update messages when the selected user or all messages change
   useEffect(() => {
     const fetchMessagesForLoggedInUserAndSelectedUser = () => {
       const userMessages = allMessages.filter(msg =>
         (msg.sender === userEmail && msg.receiver === selectedUserEmail) ||
         (msg.sender === selectedUserEmail && msg.receiver === userEmail)
       );
 
       const sortedMessages = userMessages.sort((a, b) => moment(a.timestamp, 'YYYY-MM-DD HH:mm:ss.sss').valueOf() - moment(b.timestamp, 'YYYY-MM-DD HH:mm:ss.sss').valueOf());
 
       console.log("Messages between logged-in user and selected user:", sortedMessages);
       setMessages(sortedMessages);
 
       // Mark messages as read when selected user is active
       const unreadMessages = userMessages.filter(msg => msg.receiver === userEmail && !msg.read);
       unreadMessages.forEach(msg => {
         const msgRef = ref(db, `superadmin/admins/${adminName}/chats/${msg.chatId}/${msg.messageId}`);
         update(msgRef, { read: true });
       });
 
       setNewMessagesCount(prevCount => prevCount - unreadMessages.length);
       setUsersWithUnreadMessages(prevUnreadUsers => {
         const updatedUnreadUsers = { ...prevUnreadUsers };
         delete updatedUnreadUsers[selectedUserEmail];
         return updatedUnreadUsers;
       });
     };
 
     if (selectedUserEmail) {
       fetchMessagesForLoggedInUserAndSelectedUser();
     }
   }, [allMessages, userEmail, selectedUserEmail]);
 
   // Scroll to the bottom of the message container when messages change
   useEffect(() => {
     if (messageContainerRef.current) {
       messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
     }
   }, [messages]);
 
   // Handle sending a new message
   const handleSend = () => {
     if (inputMessage.trim() !== "" && selectedUser) {
       const newMessage = {
         id: uuidv4(), // Unique message ID
         text: inputMessage,
         sender: userEmail,
         receiver: selectedUserEmail,
         timestamp: moment().utc().format('YYYY-MM-DD HH:mm:ss.sss'),
         read: false
       };
 
       console.log("Sending message:", newMessage);
 
       const receiverMessagesRef = ref(db, `superadmin/admins/${adminName}/chats/${selectedUserName}`);
       const newMessageRefForReceiver = push(receiverMessagesRef);
 
       // Set presence for the sender under the receiver's path
       const receiverPresenceRef = ref(db, `superadmin/admins/${adminName}/chats/${selectedUserName}/presence/${sanitizeEmail(userEmail)}`);
       set(receiverPresenceRef, { state: "online", last_changed: serverTimestamp() });
       onDisconnect(receiverPresenceRef).set({ state: "offline", last_changed: serverTimestamp() });
 
       set(newMessageRefForReceiver, newMessage);
       setInputMessage("");
 
       setMessages((prevMessages) => [...prevMessages, newMessage]);
     }
   };
 
   // Handle input message change
   const handleChange = (e) => {
     setInputMessage(e.target.value);
   };
 
   // Handle key press event to send message on Enter key
   const handleKeyPress = (e) => {
     if (e.key === "Enter") {
       handleSend();
     }
   };
 
   // Handle file input change
   const handleFileChange = (e) => {
     if (e.target.files[0]) {
       const file = e.target.files[0];
       handleFileUpload(file);
       setShowUploadOptions(false);
     }
   };
 
   // Handle file upload and send as a message
   const handleFileUpload = (file) => {
     if (file && selectedUser) {
       const storageRefInstance = storageRef(storage, `files/${userEmail}/${file.name}`);
       const uploadTask = uploadBytesResumable(storageRefInstance, file);
 
       uploadTask.on(
         "state_changed",
         (snapshot) => { },
         (error) => {
           console.error("File upload error:", error);
         },
         () => {
           getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
             const newMessage = {
               id: uuidv4(), // Unique message ID
               text: downloadURL,
               sender: userEmail,
               receiver: selectedUserEmail,
               timestamp: moment().utc().format('YYYY-MM-DD HH:mm:ss.sss'),
               fileType: file.type.split('/')[0],
               isFile: true,
               read: false
             };
             console.log("Sending file message:", newMessage);
 
             const receiverMessagesRef = ref(db, `superadmin/admins/${adminName}/chats/${selectedUserName}`);
             const newMessageRefForReceiver = push(receiverMessagesRef);
 
             // Set presence for the sender under the receiver's path
             const receiverPresenceRef = ref(db, `superadmin/admins/${adminName}/chats/${selectedUserName}/presence/${sanitizeEmail(userEmail)}`);
             set(receiverPresenceRef, { state: "online", last_changed: serverTimestamp() });
             onDisconnect(receiverPresenceRef).set({ state: "offline", last_changed: serverTimestamp() });
 
             set(newMessageRefForReceiver, newMessage);
             setMessages((prevMessages) => [...prevMessages, newMessage]);
           });
         }
       );
     }
   };
 
   // Handle going back to user selection
   const goBack = () => {
     setSelectedUser(null);
     setSelectedUserName("");
     setSelectedUserEmail("");
     setMessages([]);
   };
 
   // Handle removing a message
  //  const removeMessage = (messageIndex) => {
  //    setMessages(prevMessages => prevMessages.filter((_, index) => index !== messageIndex));
  //  };

//view message correction aug 24
  const removeMessage = (messageId) => {
    const updatedMessages = messages.filter(msg => msg.id !== messageId); // Filter out the specific message by its ID
    setMessages(updatedMessages);
  };

    // Optional: If you want to remove the message from the database as well
    // const messageToRemove = messages.find(msg => msg.id === messageId);
    // if (messageToRemove) {
    //   const msgRef = ref(db, `superadmin/admins/${adminName}/chats/${selectedUserName}/${messageToRemove.chatId}/${messageToRemove.messageId}`);
    //   set(msgRef, null); // Remove the message from Firebase
    // }
  // };
 
   // Toggle chat window visibility
   const toggleChatWindow = () => {
     setIsChatOpen(!isChatOpen);
     setIsMinimized(false);
     setActiveList('doctors'); // Ensure the doctors' list is shown when toggling the chat window
   };
 
   // Minimize the chat window
   const minimizeChat = () => {
     setIsMinimized(true);
     setIsChatOpen(false);
   };
 
   // Toggle upload options visibility
   const toggleUploadOptions = () => {
     setShowUploadOptions(!showUploadOptions);
   };
 
   // Handle search query change
   const handleSearch = (e) => {
     setSearchQuery(e.target.value);
   };
 
   // Filter doctor names based on search query
   const filteredDoctorNames = doctorNames.filter((user) =>
     user.name.toLowerCase().includes(searchQuery.toLowerCase())
   );
 
   // Filter technician names based on search query
   const filteredTechnicianNames = technicianNames.filter((user) =>
     user.name.toLowerCase().includes(searchQuery.toLowerCase())
   );
 
   // Render user list
   const renderUserList = (userList) => {
     return userList.map((user, index) => (
       <li key={index} onClick={() => selectUser(user)}>
         <i className="fas fa-user-md"></i>
         <h6 className="user-name">
           {user.name}
             {/* {user.email && userPresence[sanitizeEmail(user.email)] && userPresence[sanitizeEmail(user.email)].state === "online" ? 
           (
             <span className="online-dot"></span>
           ) : (
             <span className="offline-dot"></span>
           )
           } */}
         </h6>
         {usersWithUnreadMessages[user.email] && (
           <span className="unread-count">{usersWithUnreadMessages[user.email]}</span>
         )}
       </li>
     ));
   };
 
   // Group messages by date
   const groupMessagesByDate = (messages) => {
     const groupedMessages = {};
     messages.forEach((message) => {
       const date = moment.utc(message.timestamp, 'YYYY-MM-DD HH:mm:ss.sss').startOf('day').format('YYYY-MM-DD');
       if (!groupedMessages[date]) {
         groupedMessages[date] = [];
       }
       groupedMessages[date].push(message);
     });
     return groupedMessages;
   };
 
   // Render messages
   const renderMessages = (messages) => {
     const groupedMessages = groupMessagesByDate(messages);
     return Object.keys(groupedMessages).map((date) => (
       <div key={date} className="date-group">
         <div className="date-label">
           {moment(date).calendar(null, {
             sameDay: '[Today]',
             lastDay: '[Yesterday]',
             lastWeek: 'dddd',
             sameElse: 'MMMM D, YYYY'
           })}
         </div>
         {groupedMessages[date].map((msg, index) => (
           <div key={index} className={`message ${msg.sender === userEmail ? 'sent' : 'received'}`}>
             {msg.isFile ? (
               <>
                 <a href={msg.text} target="_blank" rel="noopener noreferrer">View File</a>
                 {/* <button className="remove-file-button" onClick={() => removeMessage(index)}> */}
  <button className="remove-file-button" onClick={() => removeMessage(msg.id)}> {/* view file logic correction deepa new add */}
                   <i className="fas fa-times"></i>
                 </button>
               </>
             ) : (
               <>
                 <span>{msg.text}</span>
                 <div className="message-timestamp">
                   {moment.utc(msg.timestamp).local().format('h:mm a')} {/* Convert UTC to local time */}
                 </div>
               </>
             )}
           </div>
         ))}
       </div>
     ));
   };
 




   return (
     <>
       {isMinimized && (
         <button className="chat-icon" onClick={toggleChatWindow}>
           <i className="fas fa-comments"></i>
           {newMessagesCount > 0 && (
             <span className="badge">{newMessagesCount}</span>
           )}
         </button>
       )}
       {!isMinimized && isChatOpen && (
         <div className={`chat-window ${isMinimized ? "minimized" : ""}`}>
           <div className="chat-header">
             {!selectedUser ? (
               <>
                 <h2>LiveChat</h2>
                 <button className="minimize-button" onClick={minimizeChat}>
               <i className="fas fa-window-minimize"></i>
             </button>
               </>
             ) : (
               <>
                 <span className="back-arrow" onClick={goBack}>←</span>
                 <h2>{selectedUserName}</h2>
                 <span className="close-chat" onClick={goBack}>X</span>
               </>
             )}
             
           </div>
           {!selectedUser ? (
             <>
               <div className="toggle-buttons">
                 <button onClick={() => setActiveList('doctors')} disabled={activeList === 'doctors'}>Doctors</button>
                 {userType !== 'admin' && (
                   <button onClick={() => setActiveList('admins')} disabled={activeList === 'admins'}>Admin</button>
                 )}
                 <button onClick={() => setActiveList('technicians')} disabled={activeList === 'technicians'}>Technicians</button>
               </div>
               <div className="search-bar-container">
                 {searchQuery === '' && <i className="fas fa-search search-icon"></i>}
                 <input
                   className="search-bar"
                   type="text"
                   value={searchQuery}
                   onChange={handleSearch}
                   disabled={!activeList}
                 />
               </div>
               <div className="list-container">
                 {activeList === 'doctors' && (
                   <div className="doctor-names">
                     <ul>
                       {renderUserList(filteredDoctorNames)}
                     </ul>
                   </div>
                 )}
                 {activeList === 'admins' && (
                   <div className="admin-names ">
                     <ul>
                       {renderUserList(adminNames)}
                     </ul>
                   </div>
                 )}
                 {activeList === 'technicians' && (
                   <div className="technician-names ">
                     <ul>
                       {renderUserList(filteredTechnicianNames)}
                     </ul>
                   </div>
                 )}
               </div>
             </>
           ) : (
             <div className="chat-body">
               <div className="message-container" ref={messageContainerRef}>
                 {renderMessages(messages)}
               </div>
               <div className="message-input">
                 <div className="upload-button-container">
                   <button className="upload-button" onClick={toggleUploadOptions}>
                     <i className="fas fa-plus"></i>
                   </button>
                   {showUploadOptions && (
                     <div className="upload-options">
                       <input type="file" onChange={handleFileChange} id="file-upload" style={{ display: 'none' }} />
                       <label htmlFor="file-upload" className="upload-option">
                         <i className="fas fa-file-image"></i> Photo
                       </label>
                       <label htmlFor="file-upload" className="upload-option">
                         <i className="fas fa-file"></i> Document
                       </label>
                     </div>
                   )}
                 </div>
                 <textarea
                   className="input-field"
                   value={inputMessage}
                   onChange={handleChange}
                   onKeyPress={handleKeyPress}
                   placeholder="Send a message..."
                   rows="1"
                 ></textarea>
                 <button className="send-button" onClick={handleSend}>
                   <i className="fas fa-paper-plane"></i>
                 </button>
               </div>
             </div>
           )}
         </div>
       )}
     </>
   );
 };
 
 export default ChatNot;
 
 



